import { IRule, IRuleSet } from '../../../features/detail/interfaces/IRuleResults';

/**
 * True when a rule has no meaningful outcome — empty/zero on every field
 * that would normally drive an underwriting consequence. Used to separate
 * "guideline" rules (which produce a real decision/downgrade/debit/etc.)
 * from "conditional" rules (which only match conditions).
 *
 * Single source of truth for the predicate previously duplicated as
 * `notShowRule` in RuleResults.tsx, PPWB/RuleResults.tsx, PPWB/helpers.ts.
 */
export const isEmptyRule = (rule: IRule): boolean => {
  return (
    !rule.decision &&
    !rule.classDowngrade &&
    !rule.debits &&
    !(rule.ineligibleProduct ?? []).length &&
    !(rule.ineligibleRiders ?? []).length &&
    !rule.maxCoverage &&
    !(rule.riskName ?? []).length &&
    !rule.tobaccoClass &&
    !(rule.requirement ?? []).length
  );
};

/**
 * Hygiene pipeline for rule sets returned by Maestro:
 *   1. Strip empty rules from each rule set's matched rules.
 *   2. Drop rule sets whose matched rules are now empty.
 *   3. Drop the catch-all "Default Decision" rule set.
 *   4. Drop "(superset)" rule sets — those are meta-aggregates, not guidelines.
 *
 * Returns the filtered list of "guideline" rule sets.
 */
export const filterToGuidelineRuleSets = (
  ruleSets: IRuleSet[],
): IRuleSet[] => {
  return ruleSets
    .map(ruleSet => ({
      ...ruleSet,
      matchedRules: ruleSet.matchedRules.filter(rule => !isEmptyRule(rule)),
    }))
    .filter(ruleSet => ruleSet.matchedRules.length > 0)
    .filter(ruleSet => ruleSet.ruleSetName !== 'Default Decision')
    .filter(
      ruleSet => !ruleSet.ruleSetName.toLowerCase().includes('(superset)'),
    );
};

/**
 * Strip the "(workflow)" suffix from a rule-set display name and trim
 * extra whitespace. Rule sets are sometimes named like
 * `'Medical Hypertension (workflow)'` — display surfaces should show
 * `'Medical Hypertension'`.
 */
export const stripWorkflowSuffix = (name: string): string => {
  return name.replace(/\s*\(workflow\)\s*/gi, ' ').trim();
};
